import * as React from 'react';
import { ChangeEvent, DragEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Uploader } from '../../components/Uploader';
import { uploadTemporaryFile } from '../../actions/form/uploadTemporaryFile';
import { resetFileState, setErrors } from '../../actions/form';
import { TThunkDispatch } from '../../types/redux';
import { selectForm } from '../../selectors/form';
import { validateMimeType } from './utils/validateMimeType';
import { validateSize } from './utils/validateSize';

const FILE_SIZE_ERROR = { cvFile: 'Размер файла не должен превышать 10МБ' };

export const UploaderContainer: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [dragDisabled, setDragDisabled] = useState(false);
  const dispatch = useDispatch<TThunkDispatch>();
  const {
    errors: { cvFile: error },
    isFileUploading,
    isLoading,
  } = useSelector(selectForm);

  const handleDrag = (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();

    const items = e.dataTransfer.items;

    if (!items?.length || !validateMimeType(items[0].type)) {
      setDragDisabled(true);

      return;
    }

    setDragActive(true);
  };

  const handleLeave = (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDragActive(false);
    setDragDisabled(false);
  };

  const uploadAndSet = async (file: File) => {
    if (!validateSize(file)) {
      dispatch(setErrors(FILE_SIZE_ERROR));
      setFile(null);

      return;
    }

    dispatch(setErrors({}));
    setFile(file);

    if (!(await dispatch(uploadTemporaryFile(file)))) {
      setFile(null);

      return;
    }
  };

  const handleDrop = async (e: DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    setDragActive(false);
    setDragDisabled(false);

    const files = e.dataTransfer.files;

    if (!files?.length || !validateMimeType(files[0].type)) {
      return;
    }

    await uploadAndSet(files[0]);
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;

    if (!files?.length || !validateMimeType(files[0].type)) {
      return;
    }

    await uploadAndSet(files[0]);
  };

  const clearFile = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(resetFileState());
    setFile(null);
  };

  return (
    <Uploader
      file={file}
      hasError={!!error}
      isFileUploading={isFileUploading}
      dragActive={dragActive}
      dragDisabled={dragDisabled}
      isDisabled={isLoading}
      handleDrag={handleDrag}
      handleLeave={handleLeave}
      handleDrop={handleDrop}
      handleChange={handleChange}
      handleClickCross={clearFile}
    />
  );
};

UploaderContainer.displayName = 'UploaderContainer';
